import React, { useState } from 'react';
import {
  Card,
  Button,
  Space,
  Alert,
  Typography,
  Row,
  Col,
  Spin,
  message
} from 'antd';
import {
  SafetyOutlined,
  LockOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/api';
import { openRazorpayCheckout, RazorpayCheckoutSuccess } from '../../config/razorpay';

const { Title, Text } = Typography;

export interface PaymentFormProps {
  amount: number;
  currency?: string;
  quotationRefNo: string;
  // Fired only on a genuine Razorpay success, carrying the verification triple
  // the backend re-checks (signature + amount) before creating the order.
  onPaymentSuccess: (payment: RazorpayCheckoutSuccess) => void;
  onPaymentError: (error: string) => void;
  prefill?: { name?: string; email?: string; contact?: string };
}

interface CheckoutOrderResponse {
  orderId: string;
  keyId: string;
  amount: number;
  currency: string;
}

/**
 * Razorpay payment form. Unlike the old Stripe Elements flow (embedded card
 * fields + clientSecret), Razorpay Checkout is a hosted modal:
 *   1. create an Order on the backend (returns orderId + public keyId)
 *   2. open the Checkout modal — the customer picks card/UPI/netbanking/etc.
 *   3. on success the modal returns { order_id, payment_id, signature }
 *   4. verify the signature (cheap UX check), then hand the triple to the
 *      parent, which posts it to processCheckout for authoritative server-side
 *      verification before the order is written.
 */
const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  currency = 'SGD',
  onPaymentSuccess,
  onPaymentError,
  prefill,
}) => {
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const handlePay = async () => {
    setProcessing(true);
    setPaymentError(null);

    try {
      // 1. Create the Razorpay order for the cart total.
      const orderRes = await apiClient.post<CheckoutOrderResponse>('/payments/checkout-order', {
        amount,
        currency,
      });

      if (!orderRes.success || !orderRes.data?.orderId || !orderRes.data?.keyId) {
        const msg = orderRes.message || 'Failed to initialize payment';
        setPaymentError(msg);
        onPaymentError(msg);
        setProcessing(false);
        return;
      }

      const { orderId, keyId, amount: orderAmount, currency: orderCurrency } = orderRes.data;

      // 2. Open Razorpay Checkout and wait for the result.
      let result: RazorpayCheckoutSuccess;
      try {
        result = await openRazorpayCheckout({
          keyId,
          orderId,
          amount: orderAmount,
          currency: orderCurrency,
          description: `Payment of ${currency} ${amount.toFixed(2)}`,
          prefill,
        });
      } catch (err) {
        // Dismissed or failed inside the modal.
        const msg = err instanceof Error ? err.message : 'Payment cancelled';
        if (msg !== 'Payment cancelled') {
          setPaymentError(msg);
          onPaymentError(msg);
        }
        setProcessing(false);
        return;
      }

      // 3. Cheap client-facing signature check (server re-verifies authoritatively).
      try {
        await apiClient.post('/payments/verify', {
          razorpay_order_id: result.razorpay_order_id,
          razorpay_payment_id: result.razorpay_payment_id,
          razorpay_signature: result.razorpay_signature,
        });
      } catch {
        // Non-fatal — processCheckout will do the authoritative verification.
      }

      // 4. Hand the verification triple up to the parent.
      message.success('Payment successful!');
      onPaymentSuccess(result);
    } catch (error) {
      console.error('Payment processing error:', error);
      const msg = error instanceof Error ? error.message : 'Payment processing failed';
      setPaymentError(msg);
      onPaymentError(msg);
      setProcessing(false);
    }
  };

  return (
    <Card title="Payment Information" style={{ width: '100%' }}>
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              Total Amount: {currency} {amount.toFixed(2)}
            </Title>
          </Col>
          <Col>
            <Space>
              <SafetyOutlined style={{ color: '#52c41a' }} />
              <Text type="secondary">Secure Payment</Text>
            </Space>
          </Col>
        </Row>
      </div>

      {paymentError && (
        <Alert type="error" message={paymentError} showIcon style={{ marginBottom: 16 }} />
      )}

      <Alert
        message={
          <Space>
            <LockOutlined />
            <span>You will complete payment securely via Razorpay Checkout</span>
          </Space>
        }
        type="success"
        showIcon={false}
        style={{ marginBottom: 16 }}
      />

      <Button
        type="primary"
        size="large"
        block
        loading={processing}
        disabled={processing}
        onClick={handlePay}
      >
        {processing ? (
          <Space>
            <Spin size="small" />
            <span>Processing Payment...</span>
          </Space>
        ) : (
          `Pay ${currency} ${amount.toFixed(2)}`
        )}
      </Button>
    </Card>
  );
};

export default PaymentForm;
export { PaymentForm };
